
import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { io } from 'socket.io-client';
import { WEBSOCKET_CONFIG } from '../constants/interegation-variebles';
import { useUtility } from '../context/UtilityContext';
import { useSelector } from 'react-redux';
// Create the context
const CallContext = createContext(undefined);


export const useCall = () => {
  const context = useContext(CallContext);
  if (!context) {
    throw new Error('useCall must be used within a CallProvider');
  }
  return context;
};

// CallProvider component
export const CallProvider = ({ children }) => {
  const [incomingCall, setIncomingCall] = useState({
    isOpen: false,
    callerPhone: '',
    callData: undefined,
  });

  const callSocketRef = useRef(null);
  const { selectedMetadataId } = useUtility();
  const { user } = useSelector((s) => s.authenticate);
  const companyId = user?.companyId;

  useEffect(() => {
    // don't try to connect until we have both companyId and metadata
    if (!companyId || !selectedMetadataId) return;

    // Cleanup any existing connection
    if (callSocketRef.current) {
      callSocketRef.current.disconnect();
      callSocketRef.current = null;
    }

    // Create socket connection for call events
    const callSocket = io(WEBSOCKET_CONFIG.SERVER_URL, {
      path: WEBSOCKET_CONFIG.PATH,
      transports: WEBSOCKET_CONFIG.TRANSPORTS,
      forceNew: true,
      autoConnect: true,
      timeout: 20000,
      query: {
        request: `companyId:${companyId}&metadataId:${selectedMetadataId}`,
      },
    });

    callSocketRef.current = callSocket;

    // Listen for incoming calls
    callSocket.on('call_connect', (data) => {
      console.log('Incoming call:', data);
      if (data.message && data.message.from) {
        setIncomingCall({
          isOpen: true,
          callerPhone: data.message.from,
          callData: data,
        });
      }
    });

    // Listen for call termination
    callSocket.on('call_terminate', (data) => {
      // Close the incoming call modal if termination corresponds to current call
      const termPhones = [
        data?.message?.from,
        data?.from,
      ].filter(Boolean);

      const termCallIds = [
        data?.id,
        data?.callId,
        data?.msgId,
        data?.message?.session?.id,
        data?.message?.session?.callId,
        data?.message?.id,
      ].filter(Boolean);

      setIncomingCall((prev) => {
        if (!prev.isOpen) return prev;

        const prevPhone = prev.callerPhone;
        const prevCallId = prev.callData?.id || prev.callData?.msgId || prev.callData?.callId || prev.callData?.message?.session?.id;

        const phoneMatches = prevPhone && termPhones.includes(prevPhone);
        const callIdMatches = prevCallId && termCallIds.includes(prevCallId);

        if (phoneMatches || callIdMatches) {
          return {
            isOpen: false,
            callerPhone: '',
            callData: undefined,
          };
        }

        return prev;
      });
    });

    callSocket.on('connect', () => {
      console.log('Connected to call WebSocket');
    });

    callSocket.on('disconnect', () => {
      console.log('Disconnected from call WebSocket');
    });

    // Handle page refresh/close
    const handleBeforeUnload = () => {
      console.log('Page unloading, cleaning up WebSocket');
      if (callSocketRef.current) {
        callSocketRef.current.disconnect();
        callSocketRef.current = null;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    // Cleanup on unmount
    return () => {
      console.log('CallContext cleanup');
      window.removeEventListener('beforeunload', handleBeforeUnload);
      if (callSocket) {
        callSocket.disconnect();
      }
      callSocketRef.current = null;
    };
  }, [selectedMetadataId, companyId]);

  const acceptCall = () => {
    console.log('Call accepted for:', incomingCall.callerPhone);
    // Modal will remain open for in-call controls
  };

  const declineCall = () => {
    console.log('Call declined for:', incomingCall.callerPhone);
    setIncomingCall({
      isOpen: false,
      callerPhone: '',
      callData: undefined,
    });
  };

  const closeCall = () => {
    setIncomingCall({
      isOpen: false,
      callerPhone: '',
      callData: undefined,
    });
  };

  const value = {
    incomingCall,
    acceptCall,
    declineCall,
    closeCall,
  };

  return <CallContext.Provider value={value}>{children}</CallContext.Provider>;
};
